import React, { useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import Modal, { IModalChildProps } from '../../../common/modal';
import {
    ICancelReasonItem,
    IOrderCancellation,
    IOrderDetails,
} from '../../../../models/order';
import './modal-styles.css';
import { RootState } from '../../../../store';
import { connect } from 'react-redux';
import { cancelOrder, loadOrder } from '../../../../store/order/order-actions';
import SelectInputModal from '../../../inputs/select-input-modal';
import { setIsBusy } from '../../../../store/api/api-status-action';
import { IITAPISResult } from '../../../../models/result-models';
import { gaEvent } from '../../../../hooks/ga-hook';

interface ICancelModal extends IModalChildProps {
    cancelReasonItems: ICancelReasonItem[];
    order: IOrderDetails;
    cancelOrder: (orderToCancel: IOrderCancellation) => Promise<void>;
    cancelOrderResult: IITAPISResult | null | undefined;
    loadOrder: (orderNumber: string) => Promise<void>;
    setIsBusy: (isBusy: boolean) => Promise<void>;
}

const CancelModal = ({
    onAccept,
    onClose,
    cancelReasonItems,
    order,
    cancelOrder,
    loadOrder,
    setIsBusy,
}: ICancelModal) => {
    const intl = useIntl();
    const [orderToCancel, setOrderToCancel] = useState<IOrderCancellation>({
        orderCancellation: {
            orderNumber: '',
            code: '',
            description: '',
        },
        billTo: '',
        shipTo: '',
    });
    const [selected, setSelected] = useState<number | string | null>(null);

    const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const choice = cancelReasonItems.filter(
            (item) => item.code === event.target.value
        );
        setSelected(event.target.value);
        setOrderToCancel({
            orderCancellation: {
                orderNumber: order.orderNumber,
                code: choice[0].code,
                description: choice[0].description,
            },

            billTo: order.billTo.accountNumber,
            shipTo: order.shipTo.accountNumber,
        });
    };
    const onContinue = () => {
        gaEvent(
            'Estore_Order_Updates',
            'Cancellation_Modal',
            orderToCancel.orderCancellation.description
        );
        cancelOrder(orderToCancel).then(() => {
            loadOrder(order.orderNumber).then(() => {
                setIsBusy(false);
            });
        });
        onClose();
    };

    return (
        <Modal>
            <>
                <div className="modal-header">
                    <button
                        type="button"
                        className="close"
                        id="closeModalCancelModal"
                        data-dismiss="modal"
                        aria-label={intl.formatMessage({
                            id: 'Close',
                        })}
                        onClick={onClose}
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>

                    {selected === null ? (
                        <>
                            <h2>
                                <FormattedMessage
                                    id="Sorry_to_hear_you_are_cancelling_your_order"
                                    defaultMessage="Sorry to hear you are cancelling your order."
                                />
                            </h2>
                            <p className="text-center modal-spacing">
                                <FormattedMessage
                                    id="Please_tell_us_why"
                                    defaultMessage="Please tell us why."
                                />
                            </p>
                            <label htmlFor="order-cancel">
                                <FormattedMessage
                                    id="Please_select_a_reason_for_cancelling"
                                    defaultMessage="Please select a reason for cancelling"
                                />
                            </label>

                            <SelectInputModal
                                name="description"
                                className="form-control"
                                onChange={onChange}
                                hasDefaultOption={true}
                                value={
                                    orderToCancel.orderCancellation.description
                                }
                                options={cancelReasonItems.map((option) => ({
                                    key: option.code,
                                    value: option.code,
                                    text: intl.formatMessage({
                                        id: option.description.replace(
                                            / /g,
                                            '_'
                                        ),
                                        defaultMessage: option.description,
                                    }),
                                }))}
                                defaultOption={intl.formatMessage({
                                    id: 'Select',
                                })}
                            />
                        </>
                    ) : (
                        <>
                            <p className="text-center modal-spacing">
                                <FormattedMessage
                                    id="Once_an_order_is_cancelled"
                                    defaultMessage="Once an order is cancelled, it cannot be undone."
                                />
                                <br />
                                <FormattedMessage
                                    id="You_will_need_to_place_a_new_order"
                                    defaultMessage="You will need to place a new order."
                                />
                            </p>
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    {selected === null ? (
                        <>
                            <button
                                type="button"
                                className="btn btn-default"
                                id="cancelButtonCancelModal"
                                onClick={onClose}
                            >
                                <FormattedMessage
                                    id="Close"
                                    defaultMessage="Close"
                                />
                            </button>
                            <button
                                className="btn btn-info"
                                id="cancelButtonDisabledCancelModal"
                                disabled
                            >
                                <FormattedMessage
                                    id="Continue"
                                    defaultMessage="Continue"
                                />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                id="nevermindButtonCancelModal"
                                className="btn btn-default"
                                onClick={onClose}
                            >
                                <FormattedMessage
                                    id="Nevermind"
                                    defaultMessage="Nevermind"
                                />
                            </button>
                            <button
                                className="btn btn-info"
                                id="confirmButtonCancel"
                                onClick={onContinue}
                            >
                                <FormattedMessage
                                    id="Cancel_Order"
                                    defaultMessage="Cancel Order"
                                />
                            </button>
                        </>
                    )}
                </div>
            </>
        </Modal>
    );
};
const mapStateToProps = (state: RootState) => {
    return {
        cancelOrderResult: state.cancelOrderResult
            ? state.cancelOrderResult!.data
            : null,
    };
};

const mapDispatchToProps = {
    cancelOrder,
    loadOrder,
    setIsBusy,
};

const CancelModalContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(CancelModal);

export default CancelModalContainer;
